import { Routes } from '@angular/router';
import { EnsureUserIsAuthenticated } from '@guard/ensure-user-authenticated';
import { LoginLayoutComponent } from '@layouts/login/login.layout.component';
import { BaseLayoutComponent } from '@layouts/base/base.layout.component';
import { LoginPageComponent } from '@pages/login-page/login-page.component';
import { RequestPasswordTokenComponent } from '@pages/reset-password/request/request-password-token.component';
import { UserResetPasswordComponent } from '@pages/reset-password/user-reset/user-reset-password.component';
import { ResetPasswordComponent } from '@pages/reset-password/reset/reset-password.component';
import { DeliveryRoundListComponent } from '@pages/delivery-round/list/delivery-round-list.component';
import { EnsureParentChildRelation } from '@guard/ensure-parent-child-relation';
import { EnsureHouseholdIsNotProcessing } from '@guard/ensure-household-is-not-processing';
import { EnsureClientIsNotProcessing } from '@guard/ensure-client-is-not-processing';

export const routes: Routes = [
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  {
    path: '',
    component: LoginLayoutComponent,
    children: [
      {
        path: 'login',
        component: LoginPageComponent,
      },
      {
        path: 'reset-password/request',
        component: RequestPasswordTokenComponent,
      },
      {
        path: 'reset-password/reset/:token',
        component: ResetPasswordComponent,
      },
    ],
  },
  {
    path: '',
    canActivate: [EnsureUserIsAuthenticated],
    canActivateChild: [EnsureClientIsNotProcessing],
    component: BaseLayoutComponent,
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('@pages/dashboard-page/dashboard-page.component').then(
            m => m.DashboardPageComponent
          ),
      },
      {
        path: 'foyers',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@pages/household/list/household-list.component').then(
                m => m.HouseholdListComponent
              ),
          },
          {
            path: 'create',
            loadComponent: () =>
              import('@pages/household/form/household-form.component').then(
                m => m.HouseholdFormComponent
              ),
          },
          {
            path: ':id',
            canActivate: [EnsureHouseholdIsNotProcessing],
            children: [
              {
                path: 'detail',
                loadComponent: () =>
                  import(
                    '@pages/household/detail/household-detail.component'
                  ).then(m => m.HouseholdDetailComponent),
              },
              {
                path: 'edit',
                loadComponent: () =>
                  import('@pages/household/form/household-form.component').then(
                    m => m.HouseholdFormComponent
                  ),
              },
              {
                path: 'convives/create',
                loadComponent: () =>
                  import('@pages/guest/form/guest-form.component').then(
                    m => m.GuestFormComponent
                  ),
              },
              {
                path: 'convives/:guestId/edit',
                canActivate: [EnsureParentChildRelation],
                loadComponent: () =>
                  import('@pages/guest/form/guest-form.component').then(
                    m => m.GuestFormComponent
                  ),
              },
              {
                path: 'convives/:guestId/period/create',
                loadComponent: () =>
                  import(
                    '@pages/household/detail/tabs/planification/period/form/period-form.component'
                  ).then(m => m.PeriodFormComponent),
              },
              {
                path: 'convives/:guestId/period/:periodId/edit',
                loadComponent: () =>
                  import(
                    '@pages/household/detail/tabs/planification/period/form/period-form.component'
                  ).then(m => m.PeriodFormComponent),
              },
              {
                path: 'convives/:guestId/period/bulk',
                loadComponent: () =>
                  import(
                    '@pages/household/detail/tabs/planification/period/form-bulk/period-bulk-form'
                  ).then(m => m.PeriodBulkForm),
              },
            ],
          },
        ],
      },
      {
        path: 'tournees',
        component: DeliveryRoundListComponent,
      },
      {
        path: 'import-scans',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@pages/order-scan/order-scan.component').then(
                m => m.OrderScanComponent
              ),
          },
          {
            path: 'log',
            loadComponent: () =>
              import('@pages/order-scan/log/import-scan-log.component').then(
                m => m.ImportScanLogComponent
              ),
          },
          {
            path: 'fix-errors',
            loadComponent: () =>
              import('@pages/order-scan/fix-errors/fix-scan-error').then(
                m => m.FixScanErrorComponent
              ),
          },
        ],
      },
      {
        path: 'user/:userId/user-reset',
        component: UserResetPasswordComponent,
      },
      {
        path: 'settings',
        children: [
          {
            path: 'users',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    '@pages/settings/users/list/settings-users-list.component'
                  ).then(m => m.UsersListComponent),
              },
              {
                path: 'create',
                loadComponent: () =>
                  import(
                    '@pages/settings/users/form/settings-user-form.component'
                  ).then(m => m.UserFormPageComponent),
              },
              {
                path: ':id/edit',
                loadComponent: () =>
                  import(
                    '@pages/settings/users/form/settings-user-form.component'
                  ).then(m => m.UserFormPageComponent),
              },
            ],
          },
          {
            path: 'diets',
            loadComponent: () =>
              import(
                '@pages/settings/diets/list/settings-diets-list.component'
              ).then(m => m.SettingsDietsListComponent),
          },
          {
            path: 'communes',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    '@pages/settings/communes/list/settings-communes-list.component'
                  ).then(m => m.SettingsCommunesListComponent),
              },
            ],
          },
          {
            path: 'week-models',
            loadComponent: () =>
              import(
                '@pages/settings/week-model/settings-week-model.component'
              ).then(m => m.SettingsWeekModelComponent),
          },
          {
            path: 'editions',
            loadComponent: () =>
              import(
                '@pages/settings/editions/settings-editions.component'
              ).then(m => m.SettingsEditionsComponent),
          },
          {
            path: 'delivery-rounds',
            loadComponent: () =>
              import(
                '@pages/settings/delivery-rounds/settings-delivery-rounds.component'
              ).then(m => m.SettingsDeliveryRoundsComponent),
          },
          {
            path: '',
            redirectTo: '/dashboard',
            pathMatch: 'full',
          },
        ],
      },
    ],
  },
];
